import React, { useEffect, useRef, useState } from 'react';
import { Grid, Button, Heading, VStack, HStack, Text, Progress, Box, Flex, useBreakpointValue, Spinner, useToast, Tooltip } from '@chakra-ui/react';
import { QuestionIcon, StarIcon, AtSignIcon } from '@chakra-ui/icons';
import { useSound } from '../../contexts/SoundContext.tsx';
import '../../styles/animations.css';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import TimerBar from '../Common/TimerBar.tsx';
import PlayerName from '../Common/PlayerName.tsx';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

interface MultiplayerCategoryGridProps {
  gameState: any;
  onSelectCategory: (categoryId: string) => void;
  onOpenMysterySelector: () => void;
  currentUser: any;
  categoryTimer?: { deadline: number; seconds: number; playerId: number } | null;
}

const MultiplayerCategoryGrid: React.FC<MultiplayerCategoryGridProps> = ({ 
  gameState, 
  onSelectCategory, 
  onOpenMysterySelector,
  currentUser,
  categoryTimer
}) => {
  const { t } = useTranslation();
  const { playCategorySound } = useSound();
  const toast = useToast();
  const gridColumns = useBreakpointValue({ base: 1, sm: 2, md: 3 });
  // Mélange des catégories quand le joueur est aveuglé
  const [shuffledIds, setShuffledIds] = useState<string[]>([]);
  const prevPlayerIdRef = useRef<number | null>(null);
  const shuffleArray = (ids: string[]) => {
    const arr = [...ids];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // Utiliser les catégories récupérées depuis l'API dans gameState
  const categories = gameState.categories || [];

  console.log('🎯 Catégories disponibles:', categories);
  console.log('🎮 État du jeu:', { 
    currentPlayerIndex: gameState.currentPlayerIndex,
    players: gameState.players,
    currentUser: currentUser.displayName
  });

  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const isMyTurn = currentPlayer && currentPlayer.name === currentUser.displayName;
  // Effets de statut côté serveur
  const statusEffects = (gameState as any).statusEffects || {};
  const getEffectsForPlayer = (pid?: number) => {
    const se = (gameState as any).statusEffects || {};
    // Server vertical shape
    if (se && (se.blind || se.blocked || se.translate || se.hardcore)) {
      return {
        blind: se.blind?.[pid!],
        translate: se.translate?.[pid!],
        hardcore: se.hardcore?.[pid!],
        blocked: se.blocked?.[pid!] || {},
        mysteryCooldown: se.mysteryCooldown?.[pid!] || 0
      } as any;
    }
    // Fallback: horizontal per-player shape
    const entry = (se?.[pid!] || {}) as any;
    if (entry && typeof entry.mysteryCooldown === 'number') return entry;
    return entry;
  };
  const currentEffects = getEffectsForPlayer(currentPlayer?.id);
  const isBlindedCurrent = typeof (currentEffects as any).blind === 'number' && (currentEffects as any).blind > 0;
  const mysteryCooldownTurns = Number((currentEffects as any)?.mysteryCooldown || 0);

  // Mettre à jour le mélange à chaque début de tour si aveuglé
  useEffect(() => {
    if (!gameState.players?.length) return;
  const cur = gameState.players[gameState.currentPlayerIndex];
  const eff = getEffectsForPlayer(cur?.id) as any;
  const blinded = typeof eff?.blind === 'number' && eff.blind > 0;
    const isNewTurn = prevPlayerIdRef.current !== cur?.id;
    if (isNewTurn) {
      prevPlayerIdRef.current = cur?.id ?? null;
      if (blinded) {
        setShuffledIds(shuffleArray(categories.map((c: any) => c.id)));
      } else {
        setShuffledIds([]);
      }
    } else {
      if (blinded && shuffledIds.length !== categories.length) {
        setShuffledIds(shuffleArray(categories.map((c: any) => c.id)));
      }
    }
  }, [gameState.players, gameState.currentPlayerIndex, statusEffects, categories, shuffledIds.length]);

  // Important: ne pas retourner avant l'exécution des hooks
  if (!gameState.players.length || gameState.areCategoriesLoading) {
    return (
      <VStack spacing={4} align="center">
        <Spinner size="xl" color="var(--bleu-electrique)" />
        <Text color="var(--texte-clair)">Chargement des catégories...</Text>
      </VStack>
    );
  }

  const getAttackablePlayers = () => {
    const otherPlayers = gameState.players.filter(p => p.id !== currentPlayer.id);
    return otherPlayers.filter(player => 
      Object.entries(player.score).some(([categoryId, score]) => {
        const category = categories.find(c => c.id === categoryId);
        if (!category || category.is_mystery) return false;
        
        const isCompleted = player.completedCategories.includes(categoryId);
        return (score as number) > 0 && !isCompleted;
      })
    );
  };

  // Vérifier si le joueur actuel a au moins 1 point de streak (nécessaire pour attaquer)
  const hasMinimumStreakToAttack = () => {
    if (!currentPlayer) return false;
    
    return Object.entries(currentPlayer.score).some(([categoryId, score]) => {
      const category = categories.find(c => c.id === categoryId);
      if (!category || category.is_mystery) return false;
      
      // Vérifier que la catégorie n'est pas complètement validée
      const isCompleted = currentPlayer.completedCategories.includes(categoryId);
      return (score as number) > 0 && !isCompleted;
    });
  };

  const attackablePlayers = getAttackablePlayers();
  const canUseMystery = attackablePlayers.length > 0 && hasMinimumStreakToAttack();
  const canUseMysteryNow = canUseMystery && mysteryCooldownTurns <= 0;

  const handleCategoryClick = (categoryId: string) => {
    console.log('🎯 Clic sur catégorie:', categoryId);
    console.log('🎮 Est-ce mon tour?', isMyTurn);
    console.log('👤 Joueur actuel:', currentPlayer?.name);
    console.log('👤 Utilisateur connecté:', currentUser.displayName);
    
    // Vérifier si c'est mon tour
    if (!isMyTurn) {
      console.log('⏰ Ce n\'est pas mon tour');
      return;
    }
    
    // Vérifier si la catégorie est complétée
    const { isCompleted } = getCategoryProgress(categoryId);
    if (isCompleted) {
      console.log('✅ Catégorie déjà complétée:', categoryId);
      return;
    }

    // Effet "blocked" côté client (sécurité UX, le serveur vérifiera aussi)
    const category = categories.find((c: any) => c.id === categoryId);
    const isBlocked = currentEffects?.blocked && currentEffects.blocked[categoryId] > 0;
    if (isBlocked && !category?.is_mystery) {
      toast({
        title: t('blockedCategoryTooltip') || 'Catégorie bloquée',
        description: t('categoryBlocked') || 'Cette catégorie est temporairement bloquée.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // category déjà récupérée ci-dessus
    if (category?.is_mystery) {
      // Cooldown mystère actif ? Empêcher et informer
      if (mysteryCooldownTurns > 0) {
        // Pas de toast, UX par tooltip et badge
        return;
      }
      if (canUseMysteryNow) {
        playCategorySound(categoryId);
        onOpenMysterySelector();
      } else {
        // Pas de toast, les raisons s'affichent en tooltip
        return;
      }
  } else {
      // Jouer le son seulement pour les catégories non mystère
      playCategorySound(categoryId);
      onSelectCategory(categoryId);
    }
  };

  const getCategoryProgress = (categoryId: string) => {
    const score = currentPlayer.score[categoryId] || 0;
    const isCompleted = currentPlayer.completedCategories.includes(categoryId);
    console.log(`📊 Progress pour catégorie ${categoryId}:`, { score, isCompleted, player: currentPlayer.name });
    return { score, isCompleted };
  };

  const getCategoryIcon = (category: any) => {
    if (category.is_mystery) return <AtSignIcon />;
    return <QuestionIcon />;
  };

  // Déterminer l'ordre d'affichage quand aveuglé
  const categoriesToRender = isBlindedCurrent && shuffledIds.length
    ? shuffledIds.map(id => categories.find((c: any) => c.id === id)).filter(Boolean)
    : categories;

  return (
    <VStack spacing={8} align="center" w="full" mx="auto" px={0}>
      {/* Informations du joueur actuel */}
      <VStack spacing={4} align="center">
        <Heading size="xl" color="var(--texte-clair)">
          {isMyTurn ? t('yourTurn') : t('waitingForTurn', { player: currentPlayer.name })}
        </Heading>
        <Text fontSize="lg" color="var(--texte-secondaire)">
          {isMyTurn ? t('selectCategory') : t('waitingForPlayerToSelect', { player: currentPlayer.name })}
        </Text>
      </VStack>

      {/* Scores des joueurs */}
      <HStack spacing={4} wrap="wrap" justify="center">
        {gameState.players.map((player, index) => {
          const isCurrentPlayer = index === gameState.currentPlayerIndex;
          const completedCount = player.completedCategories.filter(catId => {
            const category = categories.find(c => c.id === catId);
            return category ? !category.is_mystery : true;
          }).length;
          const totalCategories = gameState.categoriesToWin;
          // Badges d'effets
          const eff = getEffectsForPlayer(player.id) as any;
          const flashes = (statusEffects as any)?.flashEffects?.[player.id] || [];
          const badges: Array<{ key: string; text: string; bg?: string }> = [];
          if (eff.blind && eff.blind > 0) badges.push({ key: 'blind', text: `👁️ ${eff.blind}` });
          if (eff.translate && eff.translate.remaining > 0) badges.push({ key: 'translate', text: `🌍 ${eff.translate.lang?.toUpperCase?.() || ''} · ${eff.translate.remaining}` });
          if (eff.hardcore && eff.hardcore > 0) badges.push({ key: 'hardcore', text: `🔥 ${eff.hardcore}` });
          // Affichage des blocages: si une seule catégorie, montrer son nom + tours
          const blockedEntries = Object.entries(eff.blocked || {}).filter(([_, n]: any) => Number(n) > 0) as Array<[string, number]>;
          if (blockedEntries.length === 1) {
            const [catId, turns] = blockedEntries[0];
            const catName = categories.find((c: any) => c.id === catId)?.name || catId;
            badges.push({ key: `blocked-${catId}`, text: `🚫 ${catName} · ${turns}` });
          } else if (blockedEntries.length > 1) {
            const totalTurns = blockedEntries.reduce((s, [, n]) => s + (Number(n) || 0), 0);
            badges.push({ key: 'blocked-multi', text: `🚫 ${blockedEntries.length} · ${totalTurns}` });
          }
          // Ajout des effets flash 1-tour (minus/plus/swap/specials visuels)
          flashes.forEach((f: any) => {
            const fid = typeof f === 'string' ? f : f.id;
            const m = /^(minus|plus|swap)(\d+)$/.exec(fid);
            if (m) {
              const kind = m[1];
              const n = Number(m[2]);
              if (kind === 'minus') badges.push({ key: `flash-${fid}`, text: `⬇️ -${n}`, bg: '#744210' });
              if (kind === 'plus') badges.push({ key: `flash-${fid}`, text: `⬆️ +${n}`, bg: '#22543D' });
              if (kind === 'swap') badges.push({ key: `flash-${fid}`, text: `🔄 ${n}`, bg: '#2C5282' });
              return;
            }
            const map: any = { blocked: { emoji: '🚫', bg: '#63171B', label: 'Bloqué' }, blind: { emoji: '👁️‍🗨️', bg: '#1A365D', label: 'Aveugle' }, translate: { emoji: '🌍', bg: '#1C4532', label: 'Traduction' }, nuke: { emoji: '💣', bg: '#4A5568', label: 'Nuke' }, megaswap: { emoji: '🌪️', bg: '#2B6CB0', label: 'MegaSwap' }, hardcore: { emoji: '🔥', bg: '#7B341E', label: 'Hardcore' } };
            const info = map[fid] || { emoji: '✨', bg: '#2D3748', label: fid || 'Pouvoir' };
            badges.push({ key: `flash-${fid}`, text: `${info.emoji} ${info.label}` , bg: info.bg });
          });
          
          return (
            <MotionBox
              key={player.id}
              p={4}
              bg={isCurrentPlayer ? "var(--bleu-electrique)" : "var(--gris-bleute)"}
              borderRadius="lg"
              boxShadow="md"
              textAlign="center"
              minW="120px"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Box
                display="inline-block"
                bg={isCurrentPlayer ? "rgba(0,0,0,0.35)" : "transparent"}
                px={isCurrentPlayer ? 3 : 0}
                py={isCurrentPlayer ? 1 : 0}
                borderRadius="full"
                mb={2}
              >
                <Text fontSize="sm" fontWeight="bold" color="var(--texte-clair)">
                  <PlayerName
                    userId={player.uid || (player.name === currentUser.displayName ? currentUser.uid : '')}
                    userName={player.name}
                    checkEffect={!!(player.uid || (player.name === currentUser.displayName && currentUser.uid))}
                  />
                </Text>
              </Box>
              {badges.length > 0 && (
                <HStack spacing={1} justify="center" mb={1} wrap="wrap">
                  {badges.map(b => (
                    <Box key={b.key} fontSize="xs" color="white" px={2} py={0.5} borderRadius="full" bg={b.bg || 'rgba(0,0,0,0.25)'}>{b.text}</Box>
                  ))}
                </HStack>
              )}
              
              {/* Étoiles pour les catégories complétées */}
              <HStack spacing={1} justify="center">
                {[...Array(totalCategories)].map((_, starIndex) => (
                  <StarIcon
                    key={starIndex}
                    boxSize={3}
                    color={starIndex < completedCount ? "var(--or-royal)" : "rgba(255,255,255,0.3)"}
                  />
                ))}
              </HStack>
            </MotionBox>
          );
        })}
      </HStack>

      {/* Timer de sélection de catégorie – juste sous les cartes joueurs */}
      {categoryTimer && (
        <Box w="full" maxW="800px" px={4}>
          <TimerBar
            deadline={categoryTimer.deadline}
            seconds={categoryTimer.seconds}
            color={'var(--bleu-electrique)'}
            labelPrefix={t('selectCategory') || 'Choix catégorie'}
          />
        </Box>
      )}

      {/* Grille des catégories */}
      <Grid
        templateColumns={`repeat(${gridColumns}, 1fr)`}
        gap={6}
        w="full"
        maxW="800px"
        px={4}
      >
        {categoriesToRender.map((category, index) => {
          const { score, isCompleted } = getCategoryProgress(category.id);
          const progress = Math.min((score / gameState.streakToWin) * 100, 100);
          const isBlocked = currentEffects?.blocked && currentEffects.blocked[category.id] > 0;
          const blindOnThis = isBlindedCurrent && !category.is_mystery;
          const isMystery = !!category.is_mystery;
          const isMysteryDisabled = isMystery && (!canUseMysteryNow);
          const disabledReason = isMysteryDisabled && mysteryCooldownTurns > 0
            ? `Mystère indisponible · ${mysteryCooldownTurns} tour(s)`
            : undefined;
          
          // Construire le contenu d'aide (tooltip) pour Mystère quand indisponible
          const mysteryReasons: string[] = [];
          if (isMystery && mysteryCooldownTurns > 0) mysteryReasons.push(`Vous devez attendre encore ${mysteryCooldownTurns} tour(s)`);
          if (isMystery && !hasMinimumStreakToAttack()) mysteryReasons.push(`Vous n'avez avancé sur aucune catégorie`);
          if (isMystery && attackablePlayers.length === 0) mysteryReasons.push(`Pas de joueur à cibler`);

          const buttonEl = (
            <MotionBox
              key={category.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
            >
              <Button
                onClick={() => handleCategoryClick(category.id)}
                size="lg"
                h="120px"
                w="full"
                variant="solid"
                isDisabled={!isMyTurn || isMysteryDisabled || isCompleted || (isBlocked && !category.is_mystery)}
                flexDirection="column"
                justifyContent="center"
                position="relative"
                bg={isCompleted 
                  ? `linear-gradient(135deg, ${category.color}, ${category.color}DD)`
                  : blindOnThis ? `linear-gradient(135deg, rgba(128,128,128,0.4), rgba(128,128,128,0.3))` : `linear-gradient(135deg, ${category.color}66, ${category.color}44)`}
                border="3px solid"
                borderColor={isCompleted ? category.color : blindOnThis ? 'rgba(255,255,255,0.2)' : `${category.color}88`}
                color="white"
                boxShadow={isCompleted ? "xl" : "lg"}
                _hover={{
                  transform: blindOnThis ? 'none' : "translateY(-2px)",
                  boxShadow: blindOnThis ? 'lg' : "2xl",
                  bg: blindOnThis ? `linear-gradient(135deg, rgba(128,128,128,0.5), rgba(128,128,128,0.4))` : `linear-gradient(135deg, ${category.color}EE, ${category.color}CC)`,
                  borderColor: blindOnThis ? 'rgba(255,255,255,0.25)' : category.color
                }}
                _active={{
                  transform: "translateY(0px)"
                }}
                _disabled={{
                  opacity: 0.6,
                  cursor: "not-allowed",
                  _hover: {
                    transform: "none",
                    boxShadow: "lg"
                  }
                }}
              >
                <VStack spacing={2} w="full">
                  {/* Icône de la catégorie */}
                  <Box>{getCategoryIcon(category)}</Box>
                  
                  <Text fontSize="md" fontWeight="bold" textAlign="center" textShadow="1px 1px 2px rgba(0,0,0,0.5)">
                    {blindOnThis ? '???' : category.name}
                  </Text>
                  {!category.is_mystery && !blindOnThis && (
                    <Text fontSize="sm" opacity={0.9} textShadow="1px 1px 2px rgba(0,0,0,0.5)">
                      {score}/{gameState.streakToWin}
                    </Text>
                  )}
                  
                  {/* Barre de progression avec la couleur de la catégorie */}
                  {!category.is_mystery && !blindOnThis && (
                    <Box w="90%" position="relative">
                      <Progress
                        value={progress}
                        size="sm"
                        bg="rgba(0,0,0,0.3)"
                        borderRadius="full"
                        hasStripe={false}
                        isAnimated={false}
                        sx={{
                          '& > div': {
                            bg: `linear-gradient(90deg, ${category.color}FF, ${category.color}AA)`,
                            borderRadius: 'full',
                            boxShadow: `0 0 10px ${category.color}88`
                          },
                          '& .chakra-progress__label': {
                            display: 'none'
                          }
                        }}
                      />
                    </Box>
                  )}
                </VStack>
                
                {/* Étoile dorée pour les catégories complétées */}
                {isCompleted && (
                  <StarIcon
                    position="absolute"
                    top={2}
                    right={2}
                    color="var(--or-royal)"
                    boxSize={5}
                    filter="drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
                  />
                )}
                {isBlocked && !category.is_mystery && (
                  <Box position="absolute" top={2} left={2} fontSize="lg" title={t('blockedCategoryTooltip') || 'Catégorie bloquée'}>
                    🚫
                  </Box>
                )}

                {/* Compteur de cooldown Mystère */}
                {isMystery && mysteryCooldownTurns > 0 && (
                  <Box
                    position="absolute"
                    top={2}
                    right={2}
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    fontSize="xs"
                    bg="rgba(0,0,0,0.6)"
                    border="1px solid rgba(255,255,255,0.2)"
                    title={`Mystère indisponible · ${mysteryCooldownTurns} tour(s)`}
                  >
                    ⏳ {mysteryCooldownTurns}
                  </Box>
                )}
                
                {/* Effet de brillance pour les catégories mystère utilisables */}
                {category.is_mystery && canUseMysteryNow && (
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    borderRadius="md"
                    bg={`linear-gradient(45deg, transparent 30%, ${category.color}33 50%, transparent 70%)`}
                    animation="shine 2s ease-in-out infinite"
                    pointerEvents="none"
                  />
                )}
              </Button>
            </MotionBox>
          );

          // Si Mystère indisponible, afficher une info-bulle avec les raisons
          if (isMystery && isMysteryDisabled && mysteryReasons.length > 0) {
            return (
              <Tooltip
                hasArrow
                placement="top"
                shouldWrapChildren
                label={
                  <Box>
                    {mysteryReasons.map((line, i) => (
                      <Text key={i} fontSize="sm">• {line}</Text>
                    ))}
                  </Box>
                }
                openDelay={200}
              >
                {buttonEl}
              </Tooltip>
            );
          }

          return buttonEl;
        })}
      </Grid>
    </VStack>
  );
};

export default MultiplayerCategoryGrid;
